import api from './api';
import { toast } from 'sonner';

// Employee Service
export const employeeService = {
  getAll: async (params = {}) => {
    const response = await api.get('/employees', { params });
    return response;
  },
  getById: async (id) => {
    const response = await api.get(`/employees/${id}`);
    return response;
  },
  create: async (data) => {
    const response = await api.post('/employees', data);
    return response;
  },
  update: async (id, data) => {
    const response = await api.put(`/employees/${id}`, data);
    return response;
  },
  delete: async (id) => {
    const response = await api.delete(`/employees/${id}`);
    return response;
  },
  // BUG-001 FIX: Dedicated status toggle that only changes isActive (not deleted_at)
  toggleStatus: async (id, isActive) => {
    const response = await api.patch(`/employees/${id}/status`, { isActive });
    return response;
  },
  // Restore soft-deleted employee (clears deleted_at, sets isActive=true)
  restore: async (id) => {
    const response = await api.patch(`/employees/${id}/restore`);
    return response;
  },
  enrollFace: async (id, data) => {
    const response = await api.post(`/employees/${id}/enroll-face`, data);
    return response;
  },
  getEnrolledFaces: async () => {
    const response = await api.get('/employees/enrolled-faces');
    return response;
  },
  setPin: async (id, pin) => {
    const response = await api.post(`/employees/${id}/set-pin`, { pin });
    return response;
  },
  resetPin: async (id) => {
    const response = await api.post(`/employees/${id}/reset-pin`);
    return response;
  },
  uploadPhoto: async (id, photo) => {
    const response = await api.post(`/employees/${id}/photo`, { photo });
    return response;
  }
};

// Shift Service
export const shiftService = {
  getAll: async () => {
    const response = await api.get('/shifts');
    return response;
  },
  create: async (data) => {
    const response = await api.post('/shifts', data);
    return response;
  },
  update: async (id, data) => {
    const response = await api.put(`/shifts/${id}`, data);
    return response;
  },
  delete: async (id) => {
    const response = await api.delete(`/shifts/${id}`);
    return response;
  },
  toggle: async (id) => {
    const response = await api.patch(`/shifts/${id}/toggle`);
    return response;
  },
  getStats: async () => {
    const response = await api.get('/shifts/stats');
    return response;
  }
};

// Attendance Service
export const attendanceService = {
  getDaily: async (params = {}) => {
    const response = await api.get('/attendance/daily', { params });
    return response;
  },
  getEmployeeAttendance: async (empId, params = {}) => {
    const response = await api.get(`/attendance/employee/${empId}`, { params });
    return response;
  },
  getEmployeePunches: async (empId, date) => {
    const response = await api.get(`/attendance/employee/${empId}/punches/${date}`);
    return response;
  },
  recordPunch: async (data) => {
    const response = await api.post('/attendance/punch', data);
    return response;
  },
  facePunch: async (data) => {
    const response = await api.post('/attendance/face-punch', data);
    return response;
  },
  getSelfieAudit: async (params) => {
    const response = await api.get('/attendance/selfie-audit', { params });
    return response;
  },
  updatePunch: async (punchId, data) => {
    const response = await api.put(`/attendance/punch/${punchId}`, data);
    return response;
  },
  deletePunch: async (punchId) => {
    const response = await api.delete(`/attendance/punch/${punchId}`);
    return response;
  },
  addManualPunch: async (data) => {
    const response = await api.post('/attendance/manual-punch', data);
    return response;
  }
};

// Kiosk Service
export const kioskService = {
  getAll: async () => {
    const response = await api.get('/kiosks/devices');
    return response;
  },
  getById: async (id) => {
    const response = await api.get(`/kiosks/devices/${id}`);
    return response;
  },
  register: async (data) => {
    const response = await api.post('/kiosks/register', data);
    return response;
  },
  update: async (id, data) => {
    const response = await api.put(`/kiosks/devices/${id}`, data);
    return response;
  },
  delete: async (id) => {
    const response = await api.delete(`/kiosks/devices/${id}`);
    return response;
  },
  regenerateKey: async (id) => {
    const response = await api.post(`/kiosks/devices/${id}/regenerate-key`);
    return response;
  },
  getSummary: async () => {
    const response = await api.get('/kiosks/summary');
    return response;
  },
  getPunches: async (id, limit = 50) => {
    const response = await api.get(`/kiosks/devices/${id}/punches?limit=${limit}`);
    return response;
  }
};

// Dashboard Service  
export const dashboardService = {
  getTodaySummary: async () => {
    const response = await api.get('/dashboard/today-summary');
    return response;
  },
  getWeeklyTrend: async () => {
    const response = await api.get('/dashboard/weekly-trend');
    return response;
  },
  getKioskHealth: async () => {
    const response = await api.get('/dashboard/kiosk-health');
    return response;
  }
};

// Reports Service
// Apr 29, 2026 (Phase 4) — every method now accepts an optional second
// `opts` argument that's forwarded to axios. Pass `{ signal }` from
// the caller to enable AbortController cancellation. Backwards-compat:
// callers that don't pass opts continue to work unchanged.
export const reportsService = {
  getOverview: async (params = {}, opts = {}) => {
    const response = await api.get('/reports/overview', { params, ...opts });
    return response;
  },
  getMusterRoll: async (params = {}, opts = {}) => {
    const response = await api.get('/reports/muster-roll', { params, ...opts });
    return response;
  },
  getLateComing: async (params = {}, opts = {}) => {
    const response = await api.get('/reports/late-coming', { params, ...opts });
    return response;
  },
  getOvertime: async (params = {}, opts = {}) => {
    const response = await api.get('/reports/overtime', { params, ...opts });
    return response;
  },
  getDepartment: async (params = {}, opts = {}) => {
    const response = await api.get('/reports/department', { params, ...opts });
    return response;
  },
  getAbsentee: async (params = {}, opts = {}) => {
    const response = await api.get('/reports/absentee', { params, ...opts });
    return response;
  },
  getWeeklyTrend: async (params = {}, opts = {}) => {
    const response = await api.get('/reports/weekly-trend', { params, ...opts });
    return response;
  },
  getMonthlyTrend: async (params = {}, opts = {}) => {
    const response = await api.get('/reports/monthly-trend', { params, ...opts });
    return response;
  },
  getShiftWise: async (params = {}, opts = {}) => {
    const response = await api.get('/reports/shift-wise', { params, ...opts });
    return response;
  },
  getContractorWise: async (params = {}, opts = {}) => {
    const response = await api.get('/reports/contractor-wise', { params, ...opts });
    return response;
  }
};

// Department Service
export const departmentService = {
  getAll: async (forceRefresh = false) => {
    // BUG-002 FIX: Support force refresh after mutations
    const response = await api.get('/departments', { skipCache: forceRefresh });
    return response;
  },
  create: async (data) => {
    const response = await api.post('/departments', data);
    // Force clear cache immediately
    api.invalidateCache('/departments');
    return response;
  },
  update: async (id, data) => {
    const response = await api.put(`/departments/${id}`, data);
    api.invalidateCache('/departments');
    return response;
  },
  delete: async (id) => {
    const response = await api.delete(`/departments/${id}`);
    api.invalidateCache('/departments');
    return response;
  }
};

// Leave Service
export const leaveService = {
  getAll: async (params = {}) => {
    const response = await api.get('/leaves', { params });
    return response;
  },
  getStats: async (params = {}) => {
    const response = await api.get('/leaves/stats', { params });
    return response;
  },
  create: async (data) => {
    const response = await api.post('/leaves', data);
    return response;
  },
  approve: async (id, remarks) => {
    const response = await api.put(`/leaves/${id}/approve`, { remarks });
    return response;
  },
  reject: async (id, remarks) => {
    const response = await api.put(`/leaves/${id}/reject`, { remarks });
    return response;
  },
  cancel: async (id) => {
    const response = await api.put(`/leaves/${id}/cancel`);
    return response;
  },
  deptHeadApprove: async (id, remarks) => {
    const response = await api.put(`/leaves/${id}/dept-approve`, { remarks });
    return response;
  },
  deptHeadReject: async (id, remarks) => {
    const response = await api.put(`/leaves/${id}/dept-reject`, { remarks });
    return response;
  }
};

// Audit Log Service
export const auditService = {
  getAll: async (params = {}) => {
    const response = await api.get('/audit', { params });
    return response;
  }
};


// Notification Service
export const notificationService = {
  getAll: async (params = {}) => {
    const response = await api.get('/notifications', { params });
    return response;
  },
  markRead: async (id) => {
    const response = await api.put(`/notifications/${id}/read`);
    return response;
  },
  markAllRead: async () => {
    const response = await api.put('/notifications/read-all');
    return response;
  }
};

// Employee Self-Service
export const employeeSelfService = {
  getMyToday: async (empId) => {
    const response = await api.get(`/attendance/my-today/${empId}`);
    return response;
  },
  getMyHistory: async (empId, params = {}) => {
    const response = await api.get(`/attendance/my-history/${empId}`, { params });
    return response;
  }
};


// Settings Service
export const settingsService = {
  get: async () => {
    const response = await api.get('/settings');
    return response;
  },
  update: async (data) => {
    const response = await api.put('/settings', data);
    return response;
  },
  testSmtp: async (toEmail) => {
    const response = await api.post('/settings/test-smtp', { toEmail });
    return response;
  },
  processAttendance: async (date) => {
    const response = await api.post('/settings/process-attendance', { date });
    return response;
  },
  sendDailyReport: async () => {
    const response = await api.post('/settings/send-daily-report');
    return response;
  },
  // Apr 27, 2026 — manual trigger for late/absent alerts only (not the
  // full daily report). `scope` is 'current' or 'all' (all = SuperAdmin).
  sendLateAbsentAlerts: async ({ scope = 'current' } = {}) => {
    const response = await api.post('/settings/send-late-absent-alerts', { scope });
    return response;
  },
  // Sends sample Late + Absent emails to one address (dry-run of the
  // SMTP + template layer — no DB employees involved).
  sendLateAbsentAlertsTest: async (toEmail) => {
    const response = await api.post('/settings/send-late-absent-alerts/test', { toEmail });
    return response;
  },
};

// Password Reset Service
export const passwordResetService = {
  requestReset: async (email) => {
    const response = await api.post('/password-reset/request', { email });
    return response;
  },
  resetPassword: async (code, newPassword) => {
    const response = await api.post('/password-reset/reset', { code, newPassword });
    return response;
  }
};

// Anomaly Detection Service
export const anomalyService = {
  getAnomalies: async (params = {}) => {
    const response = await api.get('/anomalies', { params });
    return response;
  }
};

// Extended Service (Muster Log, Discipline, OT Alerts, Remote Punch)
export const extendedService = {
  getMusterLog: async (params = {}) => {
    const response = await api.get('/extended/muster-log', { params });
    return response;
  },
  getDisciplineReport: async (params = {}) => {
    const response = await api.get('/extended/discipline', { params });
    return response;
  },
  sendDisciplineNotifications: async (data) => {
    const response = await api.post('/extended/discipline/notify', data);
    return response;
  },
  getOTAlerts: async (params = {}) => {
    const response = await api.get('/extended/ot-alerts', { params });
    return response;
  },
  getDepartmentOTRates: async () => {
    const response = await api.get('/extended/department-ot');
    return response;
  },
  updateDepartmentOTRates: async (data) => {
    const response = await api.put('/extended/department-ot', data);
    return response;
  },
  getRemotePunchPermissions: async (params = {}) => {
    const response = await api.get('/extended/remote-punch-permissions', { params });
    return response;
  },
  updateRemotePunchPermissions: async (data) => {
    const response = await api.put('/extended/remote-punch-permissions', data);
    return response;
  },
  getPunchDetails: async (punchId) => {
    const response = await api.get(`/extended/punch/${punchId}`);
    return response;
  }
};

// Export/Download Service
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const exportService = {
  // Apr 29, 2026 (Phase 4) — file download helper.
  // Previously the .catch silently swallowed every error
  // (`.catch(() => {});`) so any backend timeout / 5xx / 4xx looked like
  // "nothing happens" to the user. Now we surface the actual status
  // code and message via toast.error, AND dismiss the pre-flight
  // "Downloading…" toast on completion so the user always knows whether
  // the download succeeded or failed.
  //
  // The `toastId` param is optional — pages that fired their own
  // pre-flight toast (e.g. MusterLogPage) can pass its id so we can
  // dismiss/replace it. If absent we generate one and own the lifecycle.
  _downloadFile: (url, filename, opts = {}) => {
    const token = localStorage.getItem('token') || localStorage.getItem('emp_token');
    const companyId = localStorage.getItem('selectedCompanyId');
    const fullUrl = `${BACKEND_URL}/api${url}`;
    const headers = { 'Authorization': `Bearer ${token}` };
    if (companyId) headers['X-Company-Id'] = companyId;
    const ownToastId = opts.toastId || `dl-${filename}`;
    if (!opts.toastId) {
      toast.loading(`Downloading ${filename}…`, { id: ownToastId });
    }
    return fetch(fullUrl, { headers })
      .then(async (res) => {
        if (!res.ok) {
          // Try to surface a real server message if the body is JSON.
          let serverMsg = '';
          try {
            const ct = res.headers.get('content-type') || '';
            if (ct.includes('application/json')) {
              const j = await res.json();
              serverMsg = j?.message || j?.error || '';
            }
          } catch (_) { /* binary or empty body */ }
          const err = new Error(serverMsg || `HTTP ${res.status} ${res.statusText || ''}`.trim());
          err.status = res.status;
          throw err;
        }
        return res.blob();
      })
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        toast.success(`Downloaded ${filename}`, { id: ownToastId, duration: 1500 });
      })
      .catch(err => {
        // Surface the failure visibly. Don't swallow.
        // eslint-disable-next-line no-console
        console.error('[downloadFile]', { url: fullUrl, filename, status: err?.status, message: err?.message });
        const msg = err?.status
          ? `Download failed (HTTP ${err.status}): ${err.message || 'Unknown error'}`
          : `Download failed: ${err?.message || 'Network error — please try again.'}`;
        toast.error(msg, { id: ownToastId, duration: 6000 });
      });
  },
  getExportUrl: (type, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${BACKEND_URL}/api/export/reports/${type}/excel?${qs}`;
  },
  downloadMusterLogExcel: (params) => {
    const qs = new URLSearchParams(params).toString();
    // Phase J (May 9, 2026) — return the underlying promise so callers
    // can show a "Preparing your download…" spinner that flips off
    // when the file is in the user's Downloads folder. _downloadFile
    // already calls toast.loading/success/error internally.
    return exportService._downloadFile(`/export/muster-log/excel?${qs}`, `MusterLog_${params.year}_${params.month}.xlsx`);
  },
  downloadMusterLogPDF: (params) => {
    const qs = new URLSearchParams(params).toString();
    return exportService._downloadFile(`/export/muster-log/pdf?${qs}`, `MusterLog_${params.year}_${params.month}.pdf`);
  },
  downloadDisciplineExcel: (params) => {
    const qs = new URLSearchParams(params).toString();
    exportService._downloadFile(`/export/discipline/excel?${qs}`, `Discipline_${params.year}_${params.month}.xlsx`);
  },
  downloadDisciplinePDF: (params) => {
    const qs = new URLSearchParams(params).toString();
    exportService._downloadFile(`/export/discipline/pdf?${qs}`, `Discipline_${params.year}_${params.month}.pdf`);
  },
  downloadOTAlertsExcel: (params) => {
    const qs = new URLSearchParams(params).toString();
    exportService._downloadFile(`/export/ot-alerts/excel?${qs}`, `OTAlerts_${params.year}_${params.month}.xlsx`);
  },
  downloadOTAlertsPDF: (params) => {
    const qs = new URLSearchParams(params).toString();
    exportService._downloadFile(`/export/ot-alerts/pdf?${qs}`, `OTAlerts_${params.year}_${params.month}.pdf`);
  },
  downloadAttendanceExcel: (date) => {
    exportService._downloadFile(`/export/attendance/excel?date=${date}`, `Attendance_${date}.xlsx`);
  },
  downloadAttendancePDF: (date) => {
    exportService._downloadFile(`/export/attendance/pdf?date=${date}`, `Attendance_${date}.pdf`);
  },
  downloadERPExcel: (params) => {
    const qs = new URLSearchParams(params).toString();
    exportService._downloadFile(`/export/reports/erp-export/excel?${qs}`, `ERP_Export_${params.fromDate}_to_${params.toDate}.xlsx`);
  },
  downloadERPPunchRegister: (arg) => {
    // C1 (Jul 2025) — accepts `{ fromDate, toDate }`. Caller is
    // AttendancePage's date-range modal. The previous silent
    // "single date → 1st of month rewrite" path is gone; callers
    // must pass an explicit range. We keep a backward-compatible
    // single-string fallback for any external scripts that still
    // pass `'YYYY-MM-DD'` — it maps to a same-day export.
    let fromDate, toDate;
    if (arg && typeof arg === 'object') {
      ({ fromDate, toDate } = arg);
    } else if (typeof arg === 'string') {
      fromDate = arg;
      toDate = arg;
    }
    if (!fromDate || !toDate) {
      throw new Error('downloadERPPunchRegister requires { fromDate, toDate }');
    }
    exportService._downloadFile(
      `/export/reports/erp-punch-register/excel?fromDate=${fromDate}&toDate=${toDate}`,
      `ERP_PunchRegister_${fromDate}_to_${toDate}.xlsx`
    );
  }
};

// Contractor Service
export const contractorService = {
  getAll: async (forceRefresh = false) => {
    // BUG-006 FIX: Support force refresh after mutations
    const response = await api.get('/contractors', { skipCache: forceRefresh });
    return response;
  },
  create: async (data) => {
    const response = await api.post('/contractors', data);
    api.invalidateCache('/contractors');
    return response;
  },
  update: async (id, data) => {
    const response = await api.put(`/contractors/${id}`, data);
    api.invalidateCache('/contractors');
    return response;
  },
  delete: async (id) => {
    const response = await api.delete(`/contractors/${id}`);
    api.invalidateCache('/contractors');
    return response;
  }
};
